import { Pipe, PipeTransform } from '@angular/core';

type ComparisonLabelMode = 'changed_field' | 'document_type';

const DOCUMENT_TYPE_LABEL_MAP: Record<string, string> = {
  doc_fatture_elettroniche: 'Fatture Elettroniche XML',
  doc_fatture_cartacee: 'Fatture Cartacee/PDF',
  doc_lul: 'LUL',
  doc_pec: 'PEC',
  doc_rentri: 'RENTRI',
  doc_libri_contabili: 'Libri Contabili',
  doc_altri_documenti: 'Altri Documenti',
  doc_amministrazione_finanziaria: 'Amministrazione Finanziaria',
  doc_altre_tipologie: 'Altre Tipologie',
  // Section slugs (used by section-level verify/compare)
  'general-data': 'Profilo organizzazione',
  contacts: 'Informazioni di Contatto',
  'fatture-elettroniche-xml': 'Fatture Elettroniche XML',
  'fatture-cartacee-pdf': 'Fatture Cartacee/PDF',
  'scritture-libri-contabili': 'Scritture Libri Contabili',
  'altri-documenti-rilevanza-contabile': 'Altri Documenti di Rilevanza Contabile',
  'dichiarazioni-comunicazioni-fisco': 'Dichiarazioni e Comunicazioni Amministrazione Finanziaria',
  'messaggi-posta-elettronica-certificata-pec': 'Messaggi PEC',
  'libro-unico-del-lavoro-lul': 'Libro Unico del Lavoro – LUL',
  'altre-tipologie-documentali': 'Altre Tipologie Documentali',
  rentri: 'RENTRI',
};

const DOCUMENT_TYPE_FIELD_LABEL_MAP: Record<string, Record<string, string>> = {
  doc_fatture_cartacee: {
    modalita_conservazione:
      "In quale modalità conservi le fatture cartacee o le fatture PDF, cioè le fatture cartacee di soggetti non obbligati all'emissione delle fatture elettroniche e le fatture cartacee estere?",
    primo_anno_conservazione_sostitutiva: 'Indicare il primo anno di conservazione sostitutiva',
  },
  doc_libri_contabili: {
    modalita_conservazione: 'Quali sono le modalità di conservazione dei libri contabili?',
  },
  doc_lul: {
    modalita_conservazione: "Qual è la modalità di conservazione scelta dall'Organizzazione?",
  },
  doc_rentri: {
    modalita_conservazione:
      'Da quale sistema viene generano il registro di carico e scarico rifiuti nel formato xml per essere inviato in conservazione?',
  },
};

const KEY_LABEL_ORGANIZATION: Record<string, string> = {
  denominazione_azienda: 'Denominazione azienda',
  piva: 'Partita IVA',
  codice_fiscale: 'Codice Fiscale',
  pec: 'Indirizzo PEC (INI-PEC)',
  indirizzo: 'Indirizzo sede legale (Via, n. civico, CAP, Comune, Provincia)',
  codice_sdi: 'Codice SDI per fatturazione elettronica',
  mail_amministrativa: 'Indirizzo mail per contatto amministrativo/contabile',
  telefono: 'Telefono',
  sito_web: 'È presente un sito web?',
  sito_web_notes: 'Sito web (Es. URL)',
  nome_rappresentante_legale: 'Nome Legale Rappresentante',
  cognome_rappresentante_legale: 'Cognome Legale Rappresentante',
  codice_fiscale_rappresentante_legale: 'Codice Fiscale Legale Rappresentante',
  ambito_attivita: 'Ambito di attività',
  numero_dipendenti: 'Numero dipendenti',
  ricavi_ultimo_bilancio: 'Ricavi ultimo bilancio depositato',
  periodo_imposta: "Periodo d’imposta coincidente con l'anno solare",
  data_inizio_periodo_imposta: 'Data Inizio periodo imposta',
  data_fine_periodo_imposta: 'Data fine periodo imposta',
  scadenza_presentazione: 'Scadenza presentazione dichiarazione dei redditi',
  manuale_conservazione: "È presente un Manuale di Conservazione dell'Organizzazione?",
  attachments: 'Allegati Manuale',
  nome: 'Nome',
  cognome: 'Cognome',
  email: 'Email',
  cellulare: 'Cell.',
  ruolo: 'Ruolo ricoperto',
};

const KEY_LABEL_CONSERVAZIONE_NESTED: Record<string, string> = {
  'dati_conservazione.ragione_sociale_conservatore': 'Indicare la ragione sociale del conservatore',
  'dati_conservazione.partita_iva_conservatore': 'Indicare la Partita IVA del conservatore',
  'dati_conservazione.ragione_sociale_soggetto_contratto':
    'Indicare la ragione sociale del soggetto con cui è stato sottoscritto il contratto di conservazione, se diverso dal conservatore',
  'dati_conservazione.contratto_conservazione_attachment':
    'Allegare il contratto sottoscritto per la conservazione.',
  'dati_conservazione.atto_delega_nomina':
    'È stato sottoscritto un atto di delega/nomina al conservatore?',
  'dati_conservazione.delega_nomina_cons_attachment': 'Allegare copia del documento.',
  'dati_conservazione.modalita_erogazione_servizio':
    'È stato definito un documento che descrive le modalità di erogazione del servizio (es. scheda tecnica/accordi di servizio)?',
  'dati_conservazione.doc_modalita_servizio_attachment': 'Allegare copia del documento.',
  'dati_conservazione.manuale_conservazione_att':
    'Allegare, se disponibile il Manuale di conservazione o Manuale del servizio di conservazione fornito dal conservatore.',
  'dati_conservazione.accesso_portale_conservazione':
    "Il conservatore mette a disposizione un portale di conservazione accessibile all'organizzazione o a un suo rappresentante?",
  'dati_conservazione.url_portale': 'Indicare il link di accesso al portale.',
  'dati_conservazione.tipologia_credenziali':
    'Qual è la tipologia di credenziali di accesso al portale di conservazione utilizzata?',
  'dati_conservazione.nome_utente':
    'Inserire nome e cognome dell’utente che accede alla tipologia documentale.',
  'dati_conservazione.ufficio': "Inserire il nome dell'ufficio.",
  'dati_conservazione.modalita_invio_conservazione':
    'Con quale modalità i documenti vengono inviati/versati in conservazione?',
  'dati_conservazione.descrizione_modalita_versamento': 'Descrizione delle modalità di versamento',
  'dati_conservazione.soggetto_effettua_conservazione':
    "Indicare il soggetto/ufficio che effettua l'invio/versamento dei documenti in conservazione",
  'dati_conservazione.regole_conservazione_pec':
    'Quali sono le regole di conservazione impostate sulla casella PEC?',
  'dati_conservazione.conservazione_mensilmente':
    'Il versamento al sistema di conservazione viene effettuato mensilmente entro la fine del mese successivo?',
  'dati_conservazione.sistemi_consultazione':
    'In quali sistemi sono consultabili le informazioni relative allo stato di conservazione dei documenti e/o i messaggi conservati?',
  'dati_conservazione.sistemi_consultazione_note': 'Altri sistemi di consultazione specificati',
  'dati_conservazione.soggetto_monitora_stato_conservazione':
    "Indicare l'ufficio/ruolo della persona che monitora le informazioni relative allo stato di conservazione dei documenti.",
  'dati_conservazione.notifiche_avvenuta_conservazione':
    "L'organizzazione riceve notifiche via e-mail in merito all'avvenuta conservazione dei documenti?",
  'dati_conservazione.periodicita_conservazione':
    'Indicare la periodicità di invio/versamento dei documenti in conservazione, se conosciuta',
  'dati_conservazione.periodicita_conservazione_note': 'Altra periodicità invio/versamento',
  'dati_conservazione.periodicita_chiusura':
    'Indicare la periodicità di chiusura dei Pacchetti di Archiviazione, se conosciuta',
  'dati_conservazione.periodicita_chiusura_note':
    'Altra periodicità chiusura Pacchetti Archiviazione',
  'dati_conservazione.modalita_generazione_pacchetti_distribuzione':
    'Esibizione a norma: indicare la modalità per generare i Pacchetti di Distribuzione',
  'dati_conservazione.modalita_generazione_pacchetti_distribuzione_note':
    'Altra modalità generazione PdD',
  'dati_conservazione.ultimo_anno_invio_conservazione':
    'Ultimo anno di invio dei documenti nel sistema di conservazione, se la tipologia documentale non è più prodotta o comunque non più conservata in modalità digitale/sostitutiva',
  'dati_conservazione.conservazione_anni_precedenti':
    'Negli anni precedenti a quello in corso, questa tipologia documentale è stata conservata da un fornitore diverso da quello attuale?',
  'dati_conservazione.ragione_sociale_precedente_conservatore':
    'Indicare la ragione sociale del precedente conservatore.',
  'dati_conservazione.anni_fiscali_precedente_conservatore':
    'Specificare le annualità conservate dal precedente conservatore.',
  'dati_conservazione.prima_annualita_conservata':
    'Indicare la prima annualità conservata nel sistema attuale',
};

const KEY_LABEL_CONSERVAZIONE_XFIR_NESTED: Record<string, string> = {
  'dati_conservazione_xfir.modalita_erogazione_servizio_xfir':
    'È stato definito un documento che descrive le modalità di erogazione del servizio (es. scheda tecnica/accordi di servizio) con indicazioni della specifica tipologia documentale?',
  'dati_conservazione_xfir.doc_modalita_servizio_attachment_xfir': 'Allegare copia del documento.',
  'dati_conservazione_xfir.modalita_invio_conservazione_xfir':
    'Con quale modalità i documenti vengono inviati/versati in conservazione?',
  'dati_conservazione_xfir.descrizione_modalita_versamento_xfir':
    'Descrizione delle modalità di versamento',
  'dati_conservazione_xfir.periodicita_conservazione_xfir':
    'Indicare la periodicità di invio/versamento dei documenti in conservazione, se conosciuta',
  'dati_conservazione_xfir.periodicita_conservazione_note_xfir':
    'Altra periodicità invio/versamento XFIR',
  'dati_conservazione_xfir.periodicita_chiusura_xfir':
    'Indicare la periodicità di chiusura dei Pacchetti di Archiviazione, se conosciuta',
  'dati_conservazione_xfir.periodicita_chiusura_note_xfir': 'Altra periodicità chiusura XFIR',
};

const KEY_LABEL_CONSERVAZIONE_FLAT: Record<string, string> = {
  ragione_sociale_conservatore: 'Indicare la ragione sociale del conservatore',
  partita_iva_conservatore: 'Indicare la Partita IVA del conservatore',
  rs_conservatore: 'Indicare il nome del conservatore',
  contratto_conservazione_attachment: 'Allegare il contratto sottoscritto per la conservazione.',
  atto_delega_nomina: 'È stato sottoscritto un atto di delega/nomina al conservatore?',
  delega_nomina_cons_attachment: 'Allegare copia del documento.',
  modalita_erogazione_servizio:
    'È stato definito un documento che descrive le modalità di erogazione del servizio (es. scheda tecnica/accordi di servizio)?',
  doc_modalita_servizio_attachment: 'Allegare copia del documento.',
  manuale_conservazione_att:
    'Allegare, se disponibile il Manuale di conservazione o Manuale del servizio di conservazione fornito dal conservatore.',
  accesso_portale_conservazione:
    "Il conservatore mette a disposizione un portale di conservazione accessibile all'organizzazione o a un suo rappresentante?",
  url_portale: 'Indicare il link di accesso al portale.',
  tipologia_credenziali:
    'Qual è la tipologia di credenziali di accesso al portale di conservazione utilizzata?',
  nome_utente: 'Inserire nome e cognome dell’utente che accede alla tipologia documentale.',
  ufficio: "Inserire il nome dell'ufficio.",
  modalita_invio_conservazione:
    'Con quale modalità i documenti vengono inviati/versati in conservazione?',
  descrizione_modalita_versamento: 'Descrizione delle modalità di versamento',
  soggetto_effettua_conservazione:
    "Indicare il soggetto/ufficio che effettua l'invio/versamento dei documenti in conservazione",
  regole_conservazione_pec: 'Quali sono le regole di conservazione impostate sulla casella PEC?',
  conservazione_mensilmente:
    'Il versamento al sistema di conservazione viene effettuato mensilmente entro la fine del mese successivo?',
  sistemi_consultazione:
    'In quali sistemi sono consultabili le informazioni relative allo stato di conservazione dei documenti e/o i messaggi conservati?',
  sistemi_consultazione_note:
    "Se 'Altro' è selezionato, specificare quali altri sistemi sono utilizzati per la consultazione.",
  soggetto_monitora_stato_conservazione:
    "Indicare l'ufficio/ruolo della persona che monitora le informazioni relative allo stato di conservazione dei documenti.",
  notifiche_avvenuta_conservazione:
    "L'organizzazione riceve notifiche via e-mail in merito all'avvenuta conservazione dei documenti?",
  periodicita_conservazione:
    'Indicare la periodicità di invio/versamento dei documenti in conservazione, se conosciuta',
  periodicita_conservazione_note:
    'Se "Altro" è selezionato, indicare la periodicità di invio/versamento dei documenti in conservazione.',
  periodicita_chiusura:
    'Indicare la periodicità di chiusura dei Pacchetti di Archiviazione, se conosciuta',
  periodicita_chiusura_note:
    'Se "Altro" è selezionato, indicare la periodicità di chiusura dei Pacchetti di Archiviazione.',
  modalita_generazione_pacchetti_distribuzione:
    'Esibizione a norma: indicare la modalità per generare i Pacchetti di Distribuzione',
  modalita_generazione_pacchetti_distribuzione_note:
    'Se "Altro" è selezionato, indicare la modalità per generare i Pacchetti di Distribuzione.',
  ultimo_anno_invio_conservazione:
    'Ultimo anno di invio dei documenti nel sistema di conservazione, se la tipologia documentale non è più prodotta o comunque non più conservata in modalità digitale/sostitutiva',
  conservazione_anni_precedenti:
    'Negli anni precedenti a quello in corso, questa tipologia documentale è stata conservata da un fornitore diverso da quello attuale?',
  ragione_sociale_precedente_conservatore:
    'Indicare la ragione sociale del precedente conservatore.',
  anni_fiscali_precedente_conservatore:
    'Specificare le annualità conservate dal precedente conservatore.',
  prima_annualita_conservata: 'Indicare la prima annualità conservata nel sistema attuale',
  modalita_erogazione_servizio_xfir:
    'È stato definito un documento che descrive le modalità di erogazione del servizio (es. scheda tecnica/accordi di servizio) con indicazioni della specifica tipologia documentale?',
  doc_modalita_servizio_attachment_xfir: 'Allegare copia del documento.',
  modalita_invio_conservazione_xfir:
    'Con quale modalità i documenti vengono inviati/versati in conservazione?',
  descrizione_modalita_versamento_xfir: 'Descrizione delle modalità di versamento',
  periodicita_conservazione_xfir:
    'Indicare la periodicità di invio/versamento dei documenti in conservazione, se conosciuta',
  periodicita_conservazione_note_xfir:
    'Se "Altro" è selezionato, indicare la periodicità di invio/versamento dei documenti in conservazione.',
  periodicita_chiusura_xfir:
    'Indicare la periodicità di chiusura dei Pacchetti di Archiviazione, se conosciuta',
  periodicita_chiusura_note_xfir:
    'Se "Altro" è selezionato, indicare la periodicità di chiusura dei Pacchetti di Archiviazione.',
};

const KEY_LABEL_FATTURE_ELETTRONICHE_XML: Record<string, string> = {
  anno: 'In quale anno avete iniziato ad emettere fatture elettroniche?',
  servizio: 'Qual è il nome del software/servizio che utilizzate per la fatturazione elettronica?',
  fornitore: 'Qual è il nome della Software House proprietaria del software?',
  rivenditore: 'Qual è il nome del rivenditore del software? (solo se diverso dal fornitore)',
  intermediario_sdi:
    'Vi avvalete di un intermediario SDI per la trasmissione delle fatture elettroniche?',
  tipologia_intermediario_sdi: 'Qual è la tipologia di intermediario SDI utilizzato?',
  tipologia_intermediario_sdi_note: "Specificare l'altra tipologia di Intermediario SDI.",
  ragione_sociale_intermediario_sdi: "Indicare la Ragione sociale dell'Intermediario SDI.",
  modalita_trasmissione:
    'Quale modalità di trasmissione utilizzate per inviare le fatture elettroniche (XML) a SDI?',
  servizio_trasmissione_agenzia_entrate:
    "L'Organizzazione ha aderito al servizio di conservazione di Agenzia delle Entrate? ",
  servizio_conservatore_esterno:
    "Avete attivato un servizio di conservazione delle fatture elettroniche (XML) tramite un conservatore esterno all'organizzazione?",
};

const KEY_LABEL_LIBRI_CONTABILI: Record<string, string> = {
  libri_contabili_tenuti: "Quali libri contabili sono tenuti dall'organizzazione?",
  modalita_conservazione: 'Modalità conservazione libri contabili',
  primo_anno_conservazione:
    'Indicare il primo anno di conservazione digitale a norma o sostitutiva',
};

const KEY_LABEL_PEC: Record<string, string> = {
  indirizzo_pec:
    "Qual è l'indirizzo di Posta Elettronica Certificata (PEC) registrato in INI-PEC come domicilio digitale dell'organizzazione?",
  ragione_sociale_provider:
    "Qual è la ragione sociale del provider che fornisce il servizio PEC all'organizzazione?",
  servizio_conservazione_attivo:
    'È attivo un servizio di conservazione digitale per i messaggi e le comunicazioni PEC?',
  anno_inizio_conservazione_pec:
    'Indicare il primo anno di conservazione digitale a norma o sostitutiva',
};

const KEY_LABEL_RENTRI_XFIR: Record<string, string> = {
  obbligo_rentri: "L'Organizzazione è obbligata ad iscriversi al RENTRI?",
  modalita_tenuta:
    'Qual è la modalità di tenuta del registro cronologico di carico e scarico in formato digitale',
  modalita_scarico_xfir:
    'Da quale sistema viene scaricata la copia finale dei Formulari di identificazione del rifiuto (XFIR)?',
  servizio_conservazione:
    "L'organizzazione ha attivato un servizio di conservazione per i registri di carico e scarico rifiuti?",
  servizio_conservazione_xfir:
    "L'organizzazione ha attivato un servizio di conservazione per i Formulari di identificazione del rifiuto (XFIR)?",
};

const KEY_LABEL_AMMINISTRAZIONE_FINANZIARIA: Record<string, string> = {
  tipologie_documentali_fisco:
    'Indicare le tipologie documentali sottoposte a conservazione digitale a norma o sostitutiva',
  anno_inizio_conservazione_fisco:
    'Indicare il primo anno di conservazione digitale a norma o sostitutiva',
};

const KEY_LABEL_LUL: Record<string, string> = {
  soggetto_conservatore:
    "Chi è il soggetto che conserva il libro unico del lavoro dell'Organizzazione?",
  comunicazione_itl:
    "È stata effettuata la comunicazione all'Ispettorato territoriale del Lavoro (ITL) competente?",
  primo_periodo_paga_sostitutiva:
    'Qual è il primo periodo di paga conservato in modalità sostitutiva?',
};

const KEY_LABEL_ALTRE_TIPOLOGIE: Record<string, string> = {
  tipologie_documentali_conservazione:
    'Indicare le tipologie documentali sottoposte a conservazione digitale a norma o sostitutiva',
  primo_anno_conservazione_sostitutiva:
    'Indicare il primo anno di conservazione digitale a norma o sostitutiva',
  tipologie_documentali:
    'Indicare le tipologie documentali sottoposte a conservazione digitale a norma o sostitutiva',
  anno_inizio_conservazione_altre:
    'Indicare il primo anno di conservazione digitale a norma o sostitutiva',
  libri_sociali_tipologie: 'Indicare le tipologie di libri sociali',
};

const KEY_LABEL_MAP: Record<string, string> = {
  ...KEY_LABEL_ORGANIZATION,
  ...KEY_LABEL_CONSERVAZIONE_NESTED,
  ...KEY_LABEL_CONSERVAZIONE_XFIR_NESTED,
  ...KEY_LABEL_CONSERVAZIONE_FLAT,
  ...KEY_LABEL_FATTURE_ELETTRONICHE_XML,
  ...KEY_LABEL_LIBRI_CONTABILI,
  ...KEY_LABEL_PEC,
  ...KEY_LABEL_RENTRI_XFIR,
  ...KEY_LABEL_AMMINISTRAZIONE_FINANZIARIA,
  ...KEY_LABEL_LUL,
  ...KEY_LABEL_ALTRE_TIPOLOGIE,
};

@Pipe({
  name: 'comparisonLabel',
})
export class ComparisonLabelPipe implements PipeTransform {
  transform(
    key: string,
    mode: ComparisonLabelMode = 'changed_field',
    documentType?: string,
  ): string {
    if (!key) return '';

    if (mode === 'document_type') {
      return DOCUMENT_TYPE_LABEL_MAP[key] ?? this.formatFallback(key);
    }

    const normalizedKey = this.normalizeFieldKey(key);

    if (documentType) {
      const scopedMap = DOCUMENT_TYPE_FIELD_LABEL_MAP[documentType];
      const scopedLabel = scopedMap?.[normalizedKey] ?? scopedMap?.[key];
      if (scopedLabel) {
        return scopedLabel;
      }
    }

    if (KEY_LABEL_MAP[key]) {
      return KEY_LABEL_MAP[key];
    }

    if (KEY_LABEL_MAP[normalizedKey]) {
      return KEY_LABEL_MAP[normalizedKey];
    }

    return this.formatFallback(normalizedKey);
  }

  private normalizeFieldKey(key: string): string {
    const withoutConservationPrefix = key
      .replace(/^dati_conservazione\./, '')
      .replace(/^dati_conservazione_xfir\./, '');

    const prefixedMatch = withoutConservationPrefix.match(/^tipi__.+?__(.+)$/);
    return prefixedMatch?.[1] ?? withoutConservationPrefix;
  }

  private formatFallback(key: string): string {
    return key
      .split('.')
      .pop()!
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
